//@ts-strict-ignore
import SortableHeaderCell, { CellSortDirection } from './cells/headerCells/SortableHeaderCell';
import { Component, CSSProperties, ReactElement } from 'react';

const React = require('react');
const shallowEqual = require('fbjs/lib/shallowEqual');
const BaseHeaderCell = require('./HeaderCell');
const getScrollbarSize = require('./getScrollbarSize');
const columnUtils = require('./ColumnUtils');

const FilterableHeaderCell = require('./cells/headerCells/FilterableHeaderCell');
const HeaderCellType = require('./HeaderCellType');
const createObjectWithProperties = require('./createObjectWithProperties');
require('styles/react-data-grid/react-data-grid-header.css');

type HeaderRowStyle = {
  overflow: string;
  width: number | string;
  height: number;
  position: string;
};

// The list of the propTypes that we want to include in the HeaderRow div
const knownDivPropertyKeys = ['width', 'height', 'style', 'onScroll'];

type IHeaderRowProps = {
  width: number | string;
  height: number;
  columns: Array<any> | Object;
  onColumnResize: Function;
  onSort: Function;
  onColumnResizeEnd: Function;
  style: HeaderRowStyle;
  sortColumn: string;
  sortDirection: CellSortDirection;
  cellRenderer: Function;
  headerCellRenderer: Function;
  filterable: boolean;
  onFilterChange: Function;
  resizing: Object;
  onScroll: Function;
  rowType: string;
  draggableHeaderCell: Function;
  onHeaderDrop: Function;
};

class HeaderRow extends Component<IHeaderRowProps> {
  static displayName = 'HeaderRow';
  private cells: Array<any> = [];

  shouldComponentUpdate(nextProps: IHeaderRowProps): boolean {
    return (
      nextProps.width !== this.props.width ||
      nextProps.height !== this.props.height ||
      nextProps.columns !== this.props.columns ||
      !shallowEqual(nextProps.style, this.props.style) ||
      this.props.sortColumn !== nextProps.sortColumn ||
      this.props.sortDirection !== nextProps.sortDirection
    );
  }

  getHeaderCellType = (column) => {
    if (column.filterable) {
      if (this.props.filterable) return HeaderCellType.FILTERABLE;
    }

    if (column.sortable && column.rowType !== 'filter') return HeaderCellType.SORTABLE;

    return HeaderCellType.NONE;
  };

  getFilterableHeaderCell = (column) => {
    let FilterRenderer = FilterableHeaderCell;
    if (column.filterRenderer !== undefined) {
      FilterRenderer = column.filterRenderer;
    }
    return <FilterRenderer {...this.props} onChange={this.props.onFilterChange} />;
  };

  getSortableHeaderCell = (column) => {
    const sortDirection = this.props.sortColumn === column.key ? this.props.sortDirection : CellSortDirection.None;
    const sortDescendingFirst = column.sortDescendingFirst === undefined ? false : column.sortDescendingFirst;

    return (
      <SortableHeaderCell
        column={column}
        columnKey={column.key}
        onSort={this.props.onSort}
        sortDirection={sortDirection}
        sortDescendingFirst={sortDescendingFirst}
      />
    );
  };

  getHeaderRenderer = (column) => {
    let renderer;
    if (column.headerRenderer && !this.props.filterable) {
      renderer = column.headerRenderer;
    } else {
      const headerCellType = this.getHeaderCellType(column);
      switch (headerCellType) {
        case HeaderCellType.SORTABLE:
          renderer = this.getSortableHeaderCell(column);
          break;
        case HeaderCellType.FILTERABLE:
          renderer = this.getFilterableHeaderCell(column);
          break;
        default:
          break;
      }
    }
    return renderer;
  };

  getStyle = (): HeaderRowStyle => {
    return {
      overflow: 'hidden',
      width: '100%',
      height: this.props.height,
      position: 'absolute',
    };
  };

  getCells = (): Array<ReactElement> => {
    const cells: Array<ReactElement> = [];
    const lockedCells: Array<ReactElement> = [];
    for (let i = 0, len = columnUtils.getSize(this.props.columns); i < len; i++) {
      const column = Object.assign({ rowType: this.props.rowType }, columnUtils.getColumn(this.props.columns, i));
      let _renderer = this.getHeaderRenderer(column);
      if (column.key === 'select-row' && this.props.rowType === 'filter') {
        _renderer = <div />;
      }
      const HeaderCell = column.draggable ? this.props.draggableHeaderCell : BaseHeaderCell;
      const cell = (
        <HeaderCell
          ref={(node) => (this.cells[i] = node)}
          key={i}
          height={this.props.height}
          column={column}
          renderer={_renderer}
          resizing={this.props.resizing === column}
          onResize={this.props.onColumnResize}
          onResizeEnd={this.props.onColumnResizeEnd}
          onHeaderDrop={this.props.onHeaderDrop}
        />
      );
      if (column.locked) {
        lockedCells.push(cell);
      } else {
        cells.push(cell);
      }
    }

    return cells.concat(lockedCells);
  };

  setScrollLeft = (scrollLeft: number) => {
    if (Array.isArray(this.props.columns)) {
      this.props.columns.forEach((column, i) => {
        if (column.locked) {
          this.cells[i].setScrollLeft(scrollLeft);
        } else if (this.cells[i] && this.cells[i].removeScroll) {
          this.cells[i].removeScroll();
        }
      });
    }
  };

  getKnownDivProps = () => {
    return createObjectWithProperties(this.props, knownDivPropertyKeys);
  };

  render(): ReactElement {
    const cellsStyle: CSSProperties = {
      width: this.props.width ? this.props.width + getScrollbarSize() : '100%',
      height: this.props.height,
      whiteSpace: 'nowrap',
      overflowX: 'hidden',
      overflowY: 'hidden',
    };

    const cells = this.getCells();
    return (
      <div {...this.getKnownDivProps()} className="react-grid-HeaderRow">
        <div style={cellsStyle}>{cells}</div>
      </div>
    );
  }
}

export default HeaderRow;
